import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { AuthorHeader } from '@/components/AuthorHeader';
import { CoverImage } from '@/components/CoverImage';
import { CheerButton } from '@/components/CheerButton';
import { CommentButton } from '@/components/CommentButton';
import { ExternalShareButton } from '@/components/ExternalShareButton';
import { ActivityCommentThread } from '@/components/ActivityCommentThread';
import { useActivityCommentCount } from '@/hooks/useActivityComments';
import { formatDistanceToNow } from 'date-fns';
import { 
  BookOpen, 
  Star, 
  MessageSquare, 
  Flame, 
  Trophy, 
  Target,
  Timer,
  TrendingUp
} from 'lucide-react';

interface ActivityCardProps {
  activity: {
    id: string;
    type: string;
    payload: any;
    created_at: string;
    book_title?: string;
    book_cover_url?: string;
    user_id: string;
    profiles?: {
      username?: string;
      name?: string;
      image?: string;
      is_private_name?: boolean;
    };
  };
}

export function ActivityCard({ activity }: ActivityCardProps) {
  const navigate = useNavigate();
  const [commentThreadOpen, setCommentThreadOpen] = useState(false);
  const commentCount = useActivityCommentCount(activity.id);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'book_finished':
        return <BookOpen className="w-4 h-4 text-green-500" />;
      case 'book_started':
        return <BookOpen className="w-4 h-4 text-blue-500" />;
      case 'rating_created':
      case 'rating_updated':
        return <Star className="w-4 h-4 text-yellow-500" />;
      case 'review_created':
        return <MessageSquare className="w-4 h-4 text-purple-500" />;
      case 'focus_session_completed':
        return <Timer className="w-4 h-4 text-orange-500" />;
      case 'reading_progress_updated':
        return <TrendingUp className="w-4 h-4 text-blue-400" />;
      case 'level_up':
        return <Trophy className="w-4 h-4 text-yellow-600" />;
      case 'streak_milestone':
        return <Flame className="w-4 h-4 text-orange-500" />;
      default:
        return <Target className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActivityMessage = (type: string, payload: any) => {
    switch (type) {
      case 'book_finished':
        return 'finished reading';
      case 'book_started':
        return 'started reading';
      case 'rating_created':
        return 'rated';
      case 'rating_updated':
        return 'updated their rating for';
      case 'review_created':
        return 'reviewed';
      case 'focus_session_completed':
        const minutes = payload?.duration_minutes || 0;
        return `completed a ${minutes} minute focus session`;
      case 'reading_progress_updated':
        const percent = payload?.progress_percent || 0;
        return `read to ${Math.round(percent)}%`;
      case 'level_up':
        const level = payload?.new_level || 0;
        const title = payload?.level_title || '';
        return `leveled up to Level ${level} - ${title}!`;
      case 'streak_milestone':
        const days = payload?.streak_days || 0;
        return `achieved a ${days} day reading streak!`;
      default:
        return 'had an activity';
    }
  };

  const getCelebrationBadge = (type: string, payload: any) => {
    if (type === 'book_finished' && payload?.books_finished_count) {
      const count = payload.books_finished_count;
      if (count % 100 === 0) return { text: `🌟 ${count}th book!`, variant: 'default' as const };
      if (count === 50) return { text: '🏆 Half Century!', variant: 'default' as const };
      if (count % 10 === 0) return { text: `🎉 ${count} books!`, variant: 'secondary' as const };
    }
    
    if (type === 'streak_milestone') {
      const days = payload?.streak_days || 0;
      if (days === 100) return { text: '💯 Century Streak!', variant: 'default' as const };
      if (days === 30) return { text: '🔥 Month Streak!', variant: 'default' as const };
      if (days === 7) return { text: '🔥 Week Streak!', variant: 'secondary' as const };
    }
    
    if (type === 'level_up') {
      const level = payload?.new_level || 0;
      if (level === 50) return { text: '👑 Master Level!', variant: 'default' as const };
      if (level === 25) return { text: '⭐ Quarter Century!', variant: 'default' as const };
      if (level % 10 === 0) return { text: `🎊 Level ${level}!`, variant: 'secondary' as const };
    }
    
    return null;
  };

  const celebration = getCelebrationBadge(activity.type, activity.payload);

  return (
    <>
      <Card className="w-full card-elevated overflow-hidden">
        <CardContent className="p-5">
          <div className="flex gap-3">
            {/* Activity Icon */}
            <div className="flex-shrink-0 mt-1">
              {getActivityIcon(activity.type)}
            </div>

            {/* Main Content */}
            <div className="flex-1 space-y-2">
              {/* User info and activity message */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={activity.profiles?.image} />
                    <AvatarFallback>
                      {activity.profiles?.name?.charAt(0) || activity.profiles?.username?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex items-center gap-1 flex-wrap text-sm">
                    <AuthorHeader
                      username={activity.profiles?.username}
                      name={activity.profiles?.name}
                      showFullFormat={false}
                    />
                    <span className="text-muted-foreground">{getActivityMessage(activity.type, activity.payload)}</span>
                    {activity.book_title && activity.type !== 'level_up' && activity.type !== 'streak_milestone' && (
                      <span className="font-medium">{activity.book_title}</span>
                    )}
                  </div>

                  {celebration && (
                    <Badge variant={celebration.variant} className="ml-1">
                      {celebration.text}
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>

              {/* Book Cover (if applicable) */}
              {activity.book_cover_url && activity.type !== 'level_up' && activity.type !== 'streak_milestone' && (
                <div className="flex items-center gap-2">
                  <CoverImage
                    bookId={activity.payload?.book_id || ''}
                    title={activity.book_title || ''}
                    coverUrl={activity.book_cover_url}
                    size="sm"
                    onClick={() => {
                      const bookId = activity.payload?.book_id;
                      if (bookId) navigate(`/book/${bookId}`);
                    }}
                  />
                </div>
              )}

              {/* Review excerpt */}
              {activity.type === 'review_created' && activity.payload?.review_excerpt && (
                <div className="text-sm text-muted-foreground italic pl-4 border-l-2 border-muted">
                  {activity.payload.review_excerpt}
                </div>
              )}

              {/* Rating display */}
              {(activity.type === 'rating_created' || activity.type === 'rating_updated') && activity.payload?.rating_star && (
                <div className="flex items-center gap-1 pl-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-3 h-3 ${
                        i < Math.floor(activity.payload.rating_star)
                          ? 'text-star fill-star'
                          : 'text-muted-foreground'
                      }`}
                    />
                  ))}
                  <span className="text-xs text-muted-foreground ml-1">
                    {activity.payload.rating_star.toFixed(1)}
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <CheerButton activityId={activity.id} />
                <CommentButton 
                  commentCount={commentCount}
                  onClick={() => setCommentThreadOpen(true)}
                />
                
                {/* Share button for milestone activities */}
                {(activity.type === 'book_finished' || activity.type === 'level_up') && activity.profiles?.username && (
                  <ExternalShareButton
                    shareType="milestone"
                    data={{
                      count: activity.type === 'level_up' 
                        ? activity.payload?.new_level 
                        : 1,
                      username: activity.profiles.username,
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                  />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comment Thread Dialog */}
      <ActivityCommentThread
        isOpen={commentThreadOpen}
        onClose={() => setCommentThreadOpen(false)}
        activityId={activity.id}
        activityDescription={`${activity.profiles?.name || activity.profiles?.username} ${getActivityMessage(activity.type, activity.payload)}${activity.book_title ? ` ${activity.book_title}` : ''}`}
      />
    </>
  );
}